import { useEffect, useMemo, useState } from 'react';
import type { AgreementType, CwAgreementSubtypeAliasDto } from '@catalyst-psa/shared';
import { AGREEMENT_TYPES, AGREEMENT_TYPE_LABELS } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwAgreementSubtypeAliases, useSetCwAgreementSubtypeAliases } from './api.js';

interface AliasRow {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  cwTypeName: string;
  agreementSubtypeId: string;
  /** PSA billing type ('' = leave unchanged on sync). */
  agreementType: string;
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `agr-subtype-alias-${aliasSeq}`;
}

interface AgreementSubtypeAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise agreement type (a dropdown) onto a PSA agreement subtype (a
 * dropdown) and, optionally, a PSA agreement billing type (a dropdown). Each row links a
 * CW agreement-type name to an existing PSA agreement subtype and an optional billing
 * type. On the agreements sync, a CW agreement's type sets the synced agreement's
 * descriptive subtype and — when a billing type is chosen — its billing type; a CW type
 * with no row leaves the subtype blank, and a row with no billing type leaves the
 * agreement's billing type unchanged.
 */
export function AgreementSubtypeAliasEditor({
  isOpen,
}: AgreementSubtypeAliasEditorProps): JSX.Element {
  const configQuery = useCwAgreementSubtypeAliases(isOpen);
  const saveAliases = useSetCwAgreementSubtypeAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwTypeName: alias.cwTypeName,
        agreementSubtypeId: alias.agreementSubtypeId,
        agreementType: alias.agreementType ?? '',
      })),
    );
  }, [config]);

  const subtypes = config?.subtypes ?? [];
  const discoveredTypes = config?.discoveredTypes ?? [];
  const live = config?.live ?? false;

  // CW type names already mapped, so we can hint which discovered ones remain.
  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwTypeName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredTypes.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (cwTypeName = ''): void => {
    setRows((prev) => [
      ...prev,
      { id: newAliasId(), cwTypeName, agreementSubtypeId: '', agreementType: '' },
    ]);
  };

  const handleSave = (): void => {
    const aliases: CwAgreementSubtypeAliasDto[] = rows
      .filter((row) => row.cwTypeName.trim() && row.agreementSubtypeId)
      .map((row) => ({
        cwTypeName: row.cwTypeName.trim(),
        agreementSubtypeId: row.agreementSubtypeId,
        agreementType: row.agreementType ? (row.agreementType as AgreementType) : null,
      }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-agreement-types';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">
          Agreement subtype &amp; type from agreement type
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          ConnectWise agreements carry an agreement type. Map each ConnectWise agreement type to a
          PSA agreement subtype and, optionally, a PSA agreement type (the billing type) below. On
          sync, an agreement&apos;s type sets its descriptive subtype and — when a type is chosen —
          its billing type. A CW type with no row here leaves the subtype blank; a row with no
          billing type leaves the agreement&apos;s billing type unchanged.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading agreement subtypes…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load agreement subtypes.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise agreement types loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise agreement types discovered yet. Configure the connection to load
              suggestions — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredTypes.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the agreement-subtype aliases. Make sure each row targets a valid
              subtype.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No agreement-subtype aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise agreement type</th>
                  <th className="py-2 pr-3 font-medium">PSA agreement subtype</th>
                  <th className="py-2 pr-3 font-medium">PSA agreement type</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwTypeName}
                        placeholder="e.g. Managed Services"
                        onChange={(e) => updateRow(row.id, { cwTypeName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.agreementSubtypeId}
                        onChange={(e) =>
                          updateRow(row.id, { agreementSubtypeId: e.target.value })
                        }
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select subtype —</option>
                        {subtypes.map((subtype) => (
                          <option key={subtype.id} value={subtype.id}>
                            {subtype.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.agreementType}
                        onChange={(e) => updateRow(row.id, { agreementType: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Leave unchanged —</option>
                        {AGREEMENT_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {AGREEMENT_TYPE_LABELS[type]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save mappings
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
